import React, { useContext, useEffect, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import {
  DEFAULT_REGION_CODE,
  DEFAULT_WORKFLOW_CLIENT_TYPE,
} from "../../constants/index";
import { WORKFLOW_CLIENT_TYPE_OPTIONS } from "../../constants/workflow-client-types";
import mainStyles from "../../styles";
import { executeWorkflowByName as executeWorkflowByNameApi } from "api/endpoints/workflows";
import { callOptionsHeaders } from "api/helpers";
import ActionPermissionWrapper from "components/shared/action-permission-wrapper";
import { SnackbarContext } from "context/snackbar-context";
import { getScUserEmail } from "helpers/get-sc-user-email";
import { getScUserName } from "helpers/get-sc-user-name";

interface ExecuteWorkflowByNameFormProps {
  onRefreshWorkflows: (workflowName: string, clientType: string) => void;
  defaultClientType?: string;
  allowClientTypeSelection?: boolean;
  disableExecution?: boolean;
}

const ExecuteWorkflowByNameForm = ({
  onRefreshWorkflows,
  defaultClientType = DEFAULT_WORKFLOW_CLIENT_TYPE,
  allowClientTypeSelection = true,
  disableExecution = false,
}: ExecuteWorkflowByNameFormProps) => {
  const classes = mainStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [formState, setFormState] = useState({
    workflowName: "",
    payload: "",
    clientType: defaultClientType,
  });

  const { showError } = useContext(SnackbarContext);
  const [scUserName, setScUserName] = useState("");
  const [scUserEmail, setScUserEmail] = useState("");

  useEffect(() => {
    setFormState((prevState) => ({
      ...prevState,
      clientType: defaultClientType,
    }));
  }, [defaultClientType]);

  useEffect(() => {
    getScUserName().then((name) => setScUserName(name));
    getScUserEmail().then((email) => setScUserEmail(email));
  }, []);

  const executeWorkFlow = () => {
    const { workflowName, payload, clientType } = formState;
    setIsLoading(true);
    executeWorkflowByNameApi(
      {
        workflowName,
        payload,
        employeeName: scUserName,
        employeeEmail: scUserEmail,
        clientType,
      },
      callOptionsHeaders(DEFAULT_REGION_CODE)
    )
      .then(() => {
        setFormState((prevState) => ({
          ...prevState,
          payload: "",
        }));
      })
      .then(() => {
        onRefreshWorkflows(workflowName, clientType);
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleTextAreaChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string
  ) => {
    const value = event.target.value;
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
    if (key === "workflowName") {
      onRefreshWorkflows(value, formState.clientType);
    }
  };

  const handleSelectChange = (
    event: SelectChangeEvent<string>,
    key: string
  ) => {
    const value = event.target.value as string;
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
    if (key === "clientType" && formState.workflowName) {
      onRefreshWorkflows(formState.workflowName, value);
    }
  };

  const ExecuteWorkflowByNameCard = () => {
    return (
      <Grid item xs={12}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="info-panel-content"
            id="info-panel-header"
            test-dataid="info-panel-summary"
          >
            <HelpIcon color="primary" />
            <Typography
              data-testid="info-panel-title"
              style={{ paddingLeft: ".25em" }}
            >
              How to use: Execute Workflow by Name
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              style={{ textAlign: "left" }}
              data-testid="info-panel-description"
            >
              <Typography variant="body1" component="p">
                Execute Workflow by Name will take a workflow name and a free
                form payload as input and execute it on the selected temporal
                client.
              </Typography>
              <ul>
                <li>
                  <b>Workflow Name</b> -{" "}
                  <Link
                    href="https://github.com/SafetyCulture/temporal-workflows/blob/25bba2479684cc423e875d6e4871b017d28a9d23/internal/workers/tools-support-worker.go#L322"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    list of workflow names
                  </Link>
                </li>
                <li>
                  <b>Workflow Client Type</b> - Choose from:
                  <ul>
                    <li>
                      <b>Governance</b>: Self-hosted temporal for governance
                      workflows
                    </li>
                    <li>
                      <b>Subscriptions</b>: Self-hosted temporal for
                      subscription workflows
                    </li>
                    <li>
                      <b>Training</b>: Self-hosted temporal for training
                      workflows
                    </li>
                    <li>
                      <b>Governance Cloud</b>: Cloud temporal for governance
                      workflows
                    </li>
                  </ul>
                </li>
              </ul>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Grid>
    );
  };

  return (
    <>
      <Grid item xs={12}>
        <Box
          component="form"
          sx={classes.spaceSearch}
          noValidate
          autoComplete="off"
        >
          <Grid item xs={12} style={{ paddingBottom: "20px" }}>
            <Box
              display="flex"
              alignItems="flex-start"
              gap={2}
              flexWrap="wrap"
              justifyContent="center"
            >
              <TextField
                id="workflow-name-input"
                label="Workflow Name"
                multiline
                maxRows={10}
                value={formState.workflowName}
                onChange={(event) => {
                  handleTextAreaChange(event, "workflowName");
                }}
                variant="outlined"
                size="small"
                disabled={isLoading}
                inputProps={{ "data-testid": "workflow-name-input" }}
                style={{ minWidth: 400 }}
              />
              {allowClientTypeSelection && (
                <FormControl
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 200 }}
                >
                  <InputLabel id="client-type-select-label">
                    Workflow Client Type
                  </InputLabel>
                  <Select
                    labelId="client-type-select-label"
                    id="client-type-select"
                    value={formState.clientType}
                    label="Workflow Client Type"
                    onChange={(event) =>
                      handleSelectChange(event, "clientType")
                    }
                    disabled={isLoading}
                    data-testid="client-type-select"
                  >
                    {WORKFLOW_CLIENT_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <ActionPermissionWrapper
                action={ActionType.EXECUTE_WORKFLOW_BY_NAME}
              >
                <LoadingButton
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => {
                    executeWorkFlow();
                  }}
                  loading={isLoading}
                  disabled={!formState.workflowName || disableExecution}
                  data-testid="execute-workflow-button"
                  sx={{ minHeight: "40px" }}
                >
                  Execute Workflow
                </LoadingButton>
              </ActionPermissionWrapper>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center">
              <TextField
                id="payload-input"
                label="Payload"
                multiline
                onChange={(event) => handleTextAreaChange(event, "payload")}
                variant="outlined"
                size="small"
                disabled={isLoading}
                inputProps={{ "data-testid": "payload-input" }}
                style={{ minWidth: 400 }}
                fullWidth
                rows={10}
                value={formState.payload}
                sx={classes.basicSpacing}
              />
            </Box>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12} style={{ paddingBottom: "20px" }}>
        <Grid container alignContent="space-between" alignItems="center">
          <ExecuteWorkflowByNameCard />
        </Grid>
      </Grid>
    </>
  );
};

export default ExecuteWorkflowByNameForm;
